import {
    type AgentJsonContent,
    type AgentMessage,
    type AIMessageContext,
    type AiMessageTypes,
    type UserThreadMapping,
} from '@diagon-alley/common';

// StreamMessage will be defined in the hook file
export interface StreamMessage {
    content: string;
    messageId?: string;
    traceId?: string;
}

export interface ExtractedKnowledgeItem {
    id: string;
    content: string;
    messageId: string;
    threadId: string;
}

export interface FeedbackItem {
    id: string;
    content: string;
    messageId: string;
    threadId: string;
    traceId: string;
}

export interface MemoryContent {
    type: AiMessageTypes.MEMORY_EXTRACTED;
    value: string[];
}

export interface FeedbackContent {
    type: AiMessageTypes.FEEDBACK;
    value: string;
}

export interface AiAnalystReducerState {
    showAISideBar: boolean;
    activeThread: UserThreadMapping | null;
    aiChatMessages: AgentMessage[];
    aiContext: AIMessageContext | null;
    streamedMessageIds: string[];
    userFeedback: Record<string, boolean>;
    extractedKnowledge: ExtractedKnowledgeItem[];
    feedbackQueue: FeedbackItem[];
    pushedInputText: string | null;
    isStreaming: boolean;
    statusText?: string;
    feedbackModalOpen: boolean;
    isLoadingActiveThreadMessages: boolean;
    isDeletingThread: boolean;
}

export type UpdateThreadPayload = Partial<
    Pick<UserThreadMapping, 'threadName'>
>;

export interface AiAnalystContext {
    state: AiAnalystReducerState;
    actions: {
        setShowAISidebar: (showAISideBar: boolean) => void;
        registerCallback: (callback: (data: AgentJsonContent) => void) => void;
        unregisterCallback: () => void;
        onSuccess: (json: AgentJsonContent) => void;
        setAiContext: (context: AIMessageContext | null) => void;
        setAIChatMessages: (messages: AgentMessage[]) => void;
        handleCreateThread: () => Promise<UserThreadMapping>;
        addStreamedMessageId: (messageId: string) => void;
        setUserFeedback: (userFeedback: Record<string, boolean>) => void;
        addToKnowledgeQueue: (
            memoryContent: MemoryContent,
            messageId: string,
            threadId: string,
        ) => void;
        removeFromKnowledgeQueue: (id: string) => void;
        acceptKnowledge: (item: ExtractedKnowledgeItem) => Promise<void>;
        rejectKnowledge: (item: ExtractedKnowledgeItem) => Promise<void>;
        updateKnowledgeContent: (id: string, content: string) => void;
        addToFeedbackQueue: (
            feedbackContent: FeedbackContent,
            messageId: string,
            threadId: string,
            traceId: string,
        ) => void;
        removeFromFeedbackQueue: (id: string) => void;
        handleSendMessage: (payload: StreamMessage) => Promise<void>;
        pushTextToIAinput: (text: string) => void;
        clearPushedAiInputText: () => void;
        abortStream: () => void;
        startNewThread: () => Promise<void>;
        openFeedbackModal: (
            messageId: string,
            threadId: string,
            traceId: string,
        ) => void;
        closeFeedbackModal: () => void;
        getCurrentFeedbackModalData: () => {
            messageId: string;
            threadId: string;
            traceId: string;
        } | null;
        setActiveThread: (threadId: string) => Promise<void>;
        deleteThread: (threadId: string) => Promise<void>;
        updateThreadById: (
            threadId: string,
            payload: UpdateThreadPayload,
        ) => Promise<void>;
    };
}

export enum ActionType {
    SET_SHOW_AI_ANALYST,
    SET_CHAT_MESSAGES,
    SET_AI_CHAT_MESSAGES,
    SET_ACTIVE_THREAD,
    SET_AI_CONTEXT,
    ADD_STREAMED_MESSAGE_ID,
    SET_USER_FEEDBACK,
    SET_EXTRACTED_KNOWLEDGE,
    ADD_TO_KNOWLEDGE_QUEUE,
    REMOVE_FROM_KNOWLEDGE_QUEUE,
    UPDATE_KNOWLEDGE_CONTENT,
    ADD_TO_FEEDBACK_QUEUE,
    REMOVE_FROM_FEEDBACK_QUEUE,
    PUSH_TEXT_TO_INPUT,
    CLEAR_PUSHED_INPUT_TEXT,
    OPEN_FEEDBACK_MODAL,
    CLOSE_FEEDBACK_MODAL,
    UPDATE_ACTIVE_THREAD,
    START_NEW_THREAD,
}
